import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Sparkles, Copy, Loader2 } from "lucide-react";
import { glass, BURNT, BURNT_GLOW, GREEN, ORANGE } from "./shared";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOwnClientId } from "@/hooks/useOwnClientId";

type Term = {
  term: string;
  clicks: number;
  impr: number;
  cpc: number;
  conv: number;
  intent: number;
  matchType: "Broad" | "Phrase" | "Exact";
  campaign: string;
};

const SearchTermsTab = ({
  mode,
  onTriggerAria,
}: {
  mode: "standard" | "advanced";
  onTriggerAria: (prompt: string, ctx: Record<string, any>) => void;
}) => {
  const [query, setQuery] = useState("");
  const [match, setMatch] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [convOnly, setConvOnly] = useState("all");
  const [bubble, setBubble] = useState<string | null>(null);

  // REAL DATA — scoped to the campaign selected in Aria's toggle. The toggle persists
  // its selection to localStorage (aria:recent-campaigns:<clientId>, active = recent[0]),
  // so we resolve it the same way Aria does and fetch that campaign's search terms.
  const { ownClientId } = useOwnClientId("SearchTermsTab");
  const [terms, setTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeName, setActiveName] = useState<string>("");

  useEffect(() => {
    if (!ownClientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // 1) Resolve the active campaign exactly like the toggle: recent[0] that still
        //    exists, else the first campaign — using the same Ads connection/token path.
        const { data: campData } = await supabase.functions.invoke("get-google-ads-campaigns", {
          body: { clientId: ownClientId, dateRange: "LAST_30_DAYS" },
        });
        const campaigns: { id: string; name: string }[] =
          campData?.ok && Array.isArray(campData.campaigns)
            ? campData.campaigns.filter((c: any) => c.id).map((c: any) => ({ id: String(c.id), name: c.name }))
            : [];
        if (campaigns.length === 0) {
          if (!cancelled) { setTerms([]); setActiveName(""); setLoading(false); }
          return;
        }
        let recent: string[] = [];
        try { recent = JSON.parse(localStorage.getItem(`aria:recent-campaigns:${ownClientId}`) || "[]"); } catch { /* ignore */ }
        const active =
          recent.map((id) => campaigns.find((c) => c.id === String(id))).find(Boolean) || campaigns[0];
        if (!active) { if (!cancelled) { setTerms([]); setLoading(false); } return; }
        if (!cancelled) setActiveName(active.name);

        // 2) Fetch that campaign's real search terms report.
        const { data } = await supabase.functions.invoke("get-search-terms", {
          body: { clientId: ownClientId, campaignId: active.id, dateRange: "LAST_30_DAYS" },
        });
        if (cancelled) return;
        if (data?.ok && Array.isArray(data.terms)) {
          setTerms(
            data.terms.map((t: any) => ({
              term: String(t.term),
              clicks: Number(t.clicks || 0),
              impr: Number(t.impr || 0),
              cpc: Number(t.cpc || 0),
              conv: Number(t.conv || 0),
              // Intent Score has no Google Ads source — left neutral, column untouched.
              intent: 0,
              matchType: t.matchType === "Exact" || t.matchType === "Phrase" ? t.matchType : "Broad",
              campaign: active.name,
            })),
          );
        } else {
          setTerms([]);
          setLoadError(data?.error || null);
        }
      } catch (e: any) {
        if (!cancelled) { setTerms([]); setLoadError(e?.message || "Failed to load search terms"); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ownClientId]);

  const campaigns = useMemo(() => Array.from(new Set(terms.map((t) => t.campaign))), [terms]);

  const rows = useMemo(() => {
    return terms.filter((t) => {
      if (query && !t.term.includes(query.toLowerCase())) return false;
      if (match !== "all" && t.matchType !== match) return false;
      if (campaign !== "all" && t.campaign !== campaign) return false;
      if (convOnly === "yes" && t.conv === 0) return false;
      if (convOnly === "no" && t.conv > 0) return false;
      return true;
    });
  }, [terms, query, match, campaign, convOnly]);

  const copyTerm = (term: string) => {
    try {
      navigator.clipboard?.writeText(term);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const handleAdd = (t: Term) => {
    if (mode === "standard") {
      setBubble(t.term);
      setTimeout(() => setBubble(null), 3500);
      return;
    }
    onTriggerAria(`Add "${t.term}" as a keyword`, {
      searchTerm: t.term,
      matchType: t.matchType,
      adGroup: `${t.campaign} › High Intent`,
      impact: Math.round(t.intent / 5),
    });
    toast.success(`ARIA is preparing "${t.term}"`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search terms…"
            className="h-9 w-64 rounded-lg border border-white/10 bg-white/[0.03] pl-8 pr-3 text-[12.5px] text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
          />
        </div>
        <select
          value={match}
          onChange={(e) => setMatch(e.target.value)}
          className="h-9 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-[12.5px] text-white focus:outline-none"
        >
          <option value="all">All match types</option>
          <option value="Broad">Broad</option>
          <option value="Phrase">Phrase</option>
          <option value="Exact">Exact</option>
        </select>
        <select
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
          className="h-9 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-[12.5px] text-white focus:outline-none"
        >
          <option value="all">All campaigns</option>
          {campaigns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={convOnly}
          onChange={(e) => setConvOnly(e.target.value)}
          className="h-9 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-[12.5px] text-white focus:outline-none"
        >
          <option value="all">Any conversions</option>
          <option value="yes">With conversions</option>
          <option value="no">Zero conversions</option>
        </select>
      </div>

      {/* Table */}
      <div data-tour-id="search-terms-table" className="overflow-hidden rounded-2xl" style={glass}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-[12.5px]">
            <thead className="bg-white/[0.02]">
              <tr>
                {["Search Term", "Clicks", "Impressions", "CPC", "Conversions", "Intent Score", "Action"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold uppercase tracking-[0.1em] text-[10.5px] text-white/45">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-white/40">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Loading search terms…
                    </span>
                  </td>
                </tr>
              ) : terms.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-white/40">
                    {loadError
                      ? `Couldn't load search terms: ${loadError}`
                      : activeName
                        ? `No search terms for “${activeName}” yet.`
                        : "No campaign selected."}
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((t) => (
                    <tr key={t.term} className="border-t border-white/5 hover:bg-white/[0.025]">
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          {/* Copy the search term to the clipboard */}
                          <button
                            type="button"
                            onClick={() => copyTerm(t.term)}
                            title="Copy search term"
                            aria-label="Copy search term"
                            className="mt-0.5 shrink-0 text-white/35 transition-colors hover:text-white"
                          >
                            <Copy size={13} />
                          </button>
                          <div className="min-w-0">
                            <div className="font-semibold text-white">{t.term}</div>
                            <div className="text-[10.5px] text-white/45">
                              {t.matchType} · {t.campaign}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white/80">{t.clicks.toLocaleString()}</td>
                      <td className="px-4 py-3 text-white/80">{t.impr.toLocaleString()}</td>
                      <td className="px-4 py-3 text-white/80">${t.cpc.toFixed(2)}</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: t.conv > 0 ? GREEN : "rgba(255,255,255,0.55)" }}>
                        {t.conv}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-white/8">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${t.intent}%`,
                                background:
                                  t.intent >= 80
                                    ? `linear-gradient(90deg, ${BURNT}, ${BURNT_GLOW})`
                                    : t.intent >= 50
                                      ? ORANGE
                                      : "rgba(255,255,255,0.3)",
                              }}
                            />
                          </div>
                          <span className="text-[11px] font-bold text-white/75">{t.intent}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <button
                            onClick={() => handleAdd(t)}
                            className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11.5px] font-bold text-white transition-transform hover:scale-[1.03] active:scale-[0.97]"
                            style={{
                              background: `${BURNT}1f`,
                              border: `1px solid ${BURNT}55`,
                              color: BURNT_GLOW,
                            }}
                          >
                            <Plus size={11} />
                            Add as Keyword
                          </button>
                          {bubble === t.term && (
                            <div
                              className="animate-fade-in absolute right-0 top-full z-30 mt-2 w-72 rounded-xl p-3 text-[11.5px] text-white"
                              style={{ ...glass, border: `1px solid ${BURNT}55` }}
                            >
                              <div className="mb-1 flex items-center gap-1.5 font-bold" style={{ color: BURNT_GLOW }}>
                                <Sparkles size={11} /> ARIA
                              </div>
                              Advanced mode is required to add keywords directly. Switch ARIA to Advanced mode to push this live.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-white/40">
                        No search terms match your filters.
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SearchTermsTab;
